import { useEffect, useState } from "react";
import { Volume2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  isAudioUnlocked,
  onAudioUnlockChange,
  unlockAudio,
  playNotificationSoundNow,
  hasUserGrantedSound,
  markSoundGranted,
  clearSoundGrant,
  setRemoteSoundGrant,
  type SoundTone,
} from "@/lib/notificationSounds";

const DISMISS_KEY = "notif_sound_prompt_dismissed";
const DISMISS_EXPIRY_DAYS = 30;

/** Mirror the grant onto the account so it survives cleared local storage. */
async function persistGrant(userId: string) {
  try {
    await supabase
      .from("notification_preferences")
      .upsert({ user_id: userId, sound_unlocked: true } as any, { onConflict: "user_id" });
  } catch {
    /* best effort */
  }
}




/**
 * Browsers block audio until the user interacts with the page. This prompt
 * gives the user an explicit tap to unlock notification sounds — the only
 * reliable way on iOS Safari — and disappears once audio is running.
 *
 * The grant is stored both in localStorage and on the account, so a context
 * that drops local storage (embedded preview, private window) still remembers
 * that the user already said yes.
 */
export default function SoundUnlockPrompt() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(isAudioUnlocked());
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [granted, setGranted] = useState(hasUserGrantedSound());
  const [tone, setTone] = useState<SoundTone>("ringtone");
  const [visible, setVisible] = useState(false);

  useEffect(() => onAudioUnlockChange(setUnlocked), []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    supabase
      .from("notification_preferences")
      .select("sound_enabled, sound_tone, sound_unlocked, sound_prompt_dismissed_at")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        const enabled = (data as any)?.sound_enabled ?? true;
        setSoundEnabled(enabled);
        setTone(((data as any)?.sound_tone ?? "ringtone") as SoundTone);

        if (!enabled) {
          // Preference turned off → forget the grant so we can prompt again later.
          clearSoundGrant();
          setGranted(false);
          return;
        }

        if ((data as any)?.sound_unlocked) {
          setRemoteSoundGrant(true);
          setGranted(true);
          // Grant already exists — resume the context silently if possible.
          void unlockAudio();
        } else if (hasUserGrantedSound()) {
          // Local grant exists but the account doesn't know yet — sync it up.
          setGranted(true);
          void persistGrant(user.id);
        }

        const dismissedAt = (data as any)?.sound_prompt_dismissed_at;
        if (dismissedAt && Date.now() - new Date(dismissedAt).getTime() < DISMISS_EXPIRY_DAYS * 86400000) {
          setVisible(false);
        }
      });
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    // Already granted: never prompt again. The warm-up listeners resume the
    // audio context on the user's first interaction.
    if (!user || unlocked || !soundEnabled || granted) {
      setVisible(false);
      return;
    }
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_EXPIRY_DAYS * 86400000) {
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      if (hasUserGrantedSound() || isAudioUnlocked()) return;
      setVisible(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [user, unlocked, soundEnabled, granted]);

  if (!visible || unlocked || granted) return null;

  const handleEnable = async () => {
    await unlockAudio();
    markSoundGranted();
    setRemoteSoundGrant(true);
    setGranted(true);
    setVisible(false);
    if (user) void persistGrant(user.id);
    await playNotificationSoundNow(tone);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {
      /* storage unavailable */
    }
    setVisible(false);
    if (user) {
      void supabase
        .from("notification_preferences")
        .upsert(
          { user_id: user.id, sound_prompt_dismissed_at: new Date().toISOString() } as any,
          { onConflict: "user_id" }
        );
    }
  };



  return (
    <div className="fixed bottom-24 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="bg-card border border-border rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2 shrink-0">
            <Volume2 className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Turn on notification sound</p>
            <p className="text-xs text-muted-foreground mt-1">
              Your browser blocks sound until you allow it. Tap once and the bell will ring for new alerts.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" onClick={handleEnable}>
                Enable sound
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Not now
              </Button>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleDismiss}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
